import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Upload, FileText, Eye, Download, Trash2, CheckCircle } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';
import { BoutonPhotographier } from '../components/scan/BoutonPhotographier';
import { openBlobInNewTab } from '../utils/fileUtils';

const ProfilTuteur = ({ user }) => {
  const [profile, setProfile] = useState({
    nom: user.nom || '',
    prenom: user.prenom || '',
    genre: '',
    date_naissance: '',
    adresse: '',
    code_postal: '',
    ville: '',
    telephone: user.telephone || '',
    email: user.email || '',
    diplome: '',
    annee_diplome: '',
    date_diplome: '',
    carte_pro_numero: '',
    carte_pro_expiration: '',
    entreprise: '',
    lien_professionnel: 'salarie',
    lien_professionnel_autre: '',
    photo: '',
    signature: '',
    experience_annees: ''
  });
  const [documents, setDocuments] = useState([]);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const signatureRef = useRef(null);
  const photoInputRef = useRef(null);

  const GENRES = [
    { value: '', label: 'Sélectionner...' },
    { value: 'M.', label: 'Monsieur' },
    { value: 'Mme', label: 'Madame' }
  ];

  useEffect(() => {
    loadProfile();
    loadDocuments();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await axios.get(`/tuteurs/${user.user_id}`);
      if (response.data.profile) {
        setProfile({ ...profile, ...response.data.profile });
      }
    } catch (error) {
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await axios.get(`/documents/${user.user_id}`);
      setDocuments(response.data);
    } catch (error) {
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`/tuteurs/${user.user_id}/profile`, profile);
      toast.success('Profil mis à jour avec succès');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      setProfile({ ...profile, photo: reader.result });
      await handleSave();
      toast.success('Photo mise à jour');
    };
    reader.readAsDataURL(file);
  };

  const handleDocumentUpload = (e, docType) => envoyerDocument(e.target.files[0], docType);

  const envoyerDocument = async (file, docType) => {
    if (!file) return;

    const existingDoc = documents.find(d => d.document_type === docType);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', user.user_id);
    formData.append('document_type', docType);

    try {
      await axios.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // L'ancien document du même type n'est retiré qu'une fois le nouveau bien enregistré
      if (existingDoc) await axios.delete(`/documents/${existingDoc.document_id}`).catch(() => {});
      toast.success('Document uploadé');
      loadDocuments();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 413 && detail) {
        toast.error(detail);
      } else {
        toast.error('Erreur lors de l\'upload');
      }
    }
  };

  const handleViewDocument = async (documentId) => {
    try {
      toast.info('Chargement...');
      const response = await axios.get(`/documents/download/${documentId}`);
      const doc = response.data;
      if (doc.file_data) {
        const byteCharacters = atob(doc.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: doc.content_type || 'application/pdf' });
        openBlobInNewTab(blob);
      }
    } catch (error) {
      toast.error("Erreur lors de l'ouverture");
    }
  };

  const handleDownloadDocument = async (documentId, filename) => {
    try {
      toast.info('Téléchargement...');
      const response = await axios.get(`/documents/download/${documentId}`);
      const doc = response.data;
      if (doc.file_data) {
        const byteCharacters = atob(doc.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: doc.content_type || 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename || doc.filename || 'document');
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`/documents/${documentId}`);
      toast.success('Document supprimé');
      loadDocuments();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSaveSignature = async () => {
    if (signatureRef.current.isEmpty()) {
      toast.error('Veuillez dessiner votre signature');
      return;
    }

    const signatureData = signatureRef.current.toDataURL();
    setProfile({ ...profile, signature: signatureData });
    await axios.put(`/tuteurs/${user.user_id}/profile`, { ...profile, signature: signatureData });
    setShowSignaturePad(false);
    toast.success('Signature enregistrée');
  };

  return (
    <div className="space-y-6" data-testid="profil-tuteur-page">
      <div>
        <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Mon Profil Tuteur</h1>
        <p className="text-slate-600">Gérez vos informations professionnelles</p>
      </div>

      {/* Photo */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Photo</h2>
        <div className="flex items-center space-x-6">
          <div className="w-24 h-32 bg-slate-100 border-2 border-slate-200 rounded-lg overflow-hidden">
            {profile.photo ? (
              <img src={profile.photo} alt="Photo" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User className="h-16 w-16 text-slate-400" />
              </div>
            )}
          </div>
          <div>
            <input 
              ref={photoInputRef}
              type="file" 
              accept="image/*" 
              onChange={handlePhotoUpload} 
              className="hidden" 
            />
            <Button onClick={() => photoInputRef.current?.click()} variant="outline">
              <Upload className="h-4 w-4 mr-2" />
              Changer la photo
            </Button>
          </div>
        </div>
      </div>

      {/* Informations */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Informations</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="genre">Genre</Label>
            <select
              id="genre"
              value={profile.genre || ''}
              onChange={(e) => setProfile({ ...profile, genre: e.target.value })}
              className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md"
            >
              {GENRES.map(g => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="prenom">Prénom</Label>
            <Input id="prenom" value={profile.prenom} onChange={(e) => setProfile({ ...profile, prenom: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="nom">Nom</Label>
            <Input id="nom" value={profile.nom} onChange={(e) => setProfile({ ...profile, nom: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="date_naissance">Date de naissance</Label>
            <Input id="date_naissance" type="date" value={profile.date_naissance} onChange={(e) => setProfile({ ...profile, date_naissance: e.target.value })} data-testid="tuteur-date-naissance" />
            {profile.date_naissance && (
              <p className="text-sm text-slate-500" data-testid="tuteur-age-display">
                Âge : {Math.floor((new Date() - new Date(profile.date_naissance)) / (365.25 * 24 * 60 * 60 * 1000))} ans
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="telephone">Téléphone</Label>
            <Input id="telephone" value={profile.telephone} onChange={(e) => setProfile({ ...profile, telephone: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={profile.email} disabled className="bg-slate-50" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="experience_annees">Nombre d'années d'expérience</Label>
            <Input id="experience_annees" type="number" min="0" value={profile.experience_annees} onChange={(e) => setProfile({ ...profile, experience_annees: e.target.value })} placeholder="Ex: 5" data-testid="tuteur-experience-annees" />
          </div>
          <div className="space-y-2 md:col-span-2 lg:col-span-3">
            <Label htmlFor="adresse">Adresse</Label>
            <Input id="adresse" value={profile.adresse} onChange={(e) => setProfile({ ...profile, adresse: e.target.value })} placeholder="Numéro et rue" data-testid="tuteur-adresse" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="code_postal">Code postal</Label>
            <Input id="code_postal" value={profile.code_postal} onChange={(e) => setProfile({ ...profile, code_postal: e.target.value })} placeholder="17110" data-testid="tuteur-code-postal" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ville">Ville</Label>
            <Input id="ville" value={profile.ville} onChange={(e) => setProfile({ ...profile, ville: e.target.value })} placeholder="Saint-Georges-de-Didonne" data-testid="tuteur-ville" />
          </div>
        </div>
      </div>

      {/* Diplôme */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Diplôme</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="diplome">Diplôme</Label>
            <Input id="diplome" value={profile.diplome} onChange={(e) => setProfile({ ...profile, diplome: e.target.value })} placeholder="BPJEPS, DEJEPS..." />
          </div>
          <div className="space-y-2">
            <Label htmlFor="date_diplome">Date d'obtention du Diplôme</Label>
            <Input id="date_diplome" type="date" value={profile.date_diplome} onChange={(e) => setProfile({ ...profile, date_diplome: e.target.value })} data-testid="tuteur-date-diplome" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="carte_pro_numero">N° de carte professionnelle</Label>
            <Input id="carte_pro_numero" value={profile.carte_pro_numero} onChange={(e) => setProfile({ ...profile, carte_pro_numero: e.target.value })} placeholder="123456789" data-testid="tuteur-carte-pro-numero" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="carte_pro_expiration">Date d'expiration de la carte pro</Label>
            <Input id="carte_pro_expiration" type="date" value={profile.carte_pro_expiration} onChange={(e) => setProfile({ ...profile, carte_pro_expiration: e.target.value })} data-testid="tuteur-carte-pro-expiration" />
          </div>
        </div>
      </div>

      {/* Entreprise dans laquelle le tuteur exerce */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Entreprise</h2>
        <div className="space-y-2">
          <Label htmlFor="entreprise">Nom de l'entreprise dans laquelle vous exercez</Label>
          <Input
            id="entreprise"
            value={profile.entreprise}
            onChange={(e) => setProfile({ ...profile, entreprise: e.target.value })}
            placeholder="Ex: Gap Océan, NauticPro..."
            data-testid="tuteur-entreprise"
          />
        </div>
      </div>

      {/* Lien Professionnel */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Lien Professionnel avec l'Entreprise</h2>
        <div className="space-y-4">
          <div className="flex flex-col space-y-2">
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                value="salarie"
                checked={profile.lien_professionnel === 'salarie'}
                onChange={(e) => setProfile({ ...profile, lien_professionnel: e.target.value })}
                className="w-4 h-4 text-[#8DC63F]"
              />
              <span>Salarié</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                value="auto-entrepreneur"
                checked={profile.lien_professionnel === 'auto-entrepreneur'}
                onChange={(e) => setProfile({ ...profile, lien_professionnel: e.target.value })}
                className="w-4 h-4 text-[#8DC63F]"
              />
              <span>Auto-entrepreneur</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="radio"
                value="autre"
                checked={profile.lien_professionnel === 'autre'}
                onChange={(e) => setProfile({ ...profile, lien_professionnel: e.target.value })}
                className="w-4 h-4 text-[#8DC63F]"
              />
              <span>Autre</span>
            </label>
          </div>
          {profile.lien_professionnel === 'autre' && (
            <div className="space-y-2">
              <Label htmlFor="lien_autre">Précisez</Label>
              <Input
                id="lien_autre"
                value={profile.lien_professionnel_autre}
                onChange={(e) => setProfile({ ...profile, lien_professionnel_autre: e.target.value })}
                placeholder="Précisez votre lien professionnel"
              />
            </div>
          )}
        </div>
      </div>

      {/* Documents */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="h-5 w-5 text-blue-500" />
          <h2 className="text-xl font-display font-semibold text-[#0F172A]">Documents</h2>
        </div>
        <div className="overflow-hidden border border-slate-200 rounded-lg" data-testid="tuteur-documents-table">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">Type</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">Fichier</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {[
                { type: 'diplome', label: 'Diplôme' },
                { type: 'carte_pro', label: 'Carte Professionnelle' },
                { type: 'permis', label: 'Permis' }
              ].map((docType) => {
                const existingDoc = documents.find(d => d.document_type === docType.type);
                return (
                  <tr key={docType.type} className={existingDoc ? 'bg-green-50/30' : ''} data-testid={`tuteur-doc-row-${docType.type}`}>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-slate-700">{docType.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      {existingDoc ? (
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm text-slate-700 truncate max-w-[200px]">{existingDoc.filename}</p>
                            {existingDoc.uploaded_at && (
                              <p className="text-xs text-slate-400">{new Date(existingDoc.uploaded_at).toLocaleDateString('fr-FR')}</p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400 italic">Non téléversé</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {existingDoc && (
                          <>
                            <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => handleViewDocument(existingDoc.document_id)} title="Voir" data-testid={`tuteur-view-${docType.type}`}>
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownloadDocument(existingDoc.document_id, existingDoc.filename)} title="Télécharger" data-testid={`tuteur-download-${docType.type}`}>
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDeleteDocument(existingDoc.document_id)} title="Supprimer" data-testid={`tuteur-delete-${docType.type}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                        <BoutonPhotographier doc={{ id: docType.type, nom: docType.label }} onFichier={(f) => envoyerDocument(f, docType.type)} className="h-7" />
                        <div className="relative">
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleDocumentUpload(e, docType.type)}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            data-testid={`tuteur-doc-input-${docType.type}`}
                          />
                          <Button size="sm" className={`h-7 pointer-events-none ${existingDoc ? 'bg-slate-500 hover:bg-slate-600' : 'bg-[#8DC63F] hover:bg-[#7db336]'} text-white`} data-testid={`tuteur-doc-button-${docType.type}`}>
                            <Upload className="h-3.5 w-3.5 mr-1" />
                            {existingDoc ? 'Remplacer' : 'Téléverser'}
                          </Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Signature */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4">Signature</h2>
        {!showSignaturePad && profile.signature ? (
          <div>
            <div className="border border-slate-200 rounded-lg p-4 inline-block">
              <img src={profile.signature} alt="Signature" className="h-24" />
            </div>
            <div className="mt-4">
              <Button onClick={() => setShowSignaturePad(true)} variant="outline">
                Modifier la signature
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div className="border-2 border-slate-200 rounded-lg overflow-hidden">
              <SignatureCanvas
                ref={signatureRef}
                canvasProps={{ className: 'w-full h-48 bg-white' }}
              />
            </div>
            <div className="flex space-x-3 mt-4">
              <Button onClick={handleSaveSignature} className="bg-[#8DC63F] hover:bg-[#7db336]">
                Enregistrer
              </Button>
              <Button onClick={() => signatureRef.current.clear()} variant="outline">
                Effacer
              </Button>
              {profile.signature && (
                <Button onClick={() => setShowSignaturePad(false)} variant="outline">
                  Annuler
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSave} className="bg-[#8DC63F] hover:bg-[#7db336] text-white px-8">
          Enregistrer les modifications
        </Button>
      </div>
    </div>
  );
};

export default ProfilTuteur;
